import { useState, useEffect, useRef } from 'react';
import './AirBrakeSkillsTestPro.css';

// ===== AUDIO EFFECTS =====
const playSneezeSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const time = audioContext.currentTime;

    // Sneeze: High-pitched pop that drops quickly
    const osc1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    const filter1 = audioContext.createBiquadFilter();

    osc1.type = 'sine';
    osc1.connect(filter1);
    filter1.connect(gain1);
    gain1.connect(audioContext.destination);

    osc1.frequency.setValueAtTime(1200, time);
    osc1.frequency.exponentialRampToValueAtTime(400, time + 0.1);
    gain1.gain.setValueAtTime(0.4, time);
    gain1.gain.exponentialRampToValueAtTime(0.01, time + 0.1);

    osc1.start(time);
    osc1.stop(time + 0.1);
  } catch (e) {
    console.log('Audio unavailable');
  }
};

const playBusEngineSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const time = audioContext.currentTime;

    // Bus engine: Low rumble
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();

    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(audioContext.destination);

    osc.frequency.setValueAtTime(120, time);
    gain.gain.setValueAtTime(0.2, time);
    gain.gain.linearRampToValueAtTime(0.15, time + 0.3);

    osc.start(time);
    osc.stop(time + 0.3);
  } catch (e) {
    console.log('Audio unavailable');
  }
};

const MAIN_STEPS = [
  { id: 1, title: "Securing The Bus", desc: "Park safely, apply chocks, door master off, start engine" },
  { id: 2, title: "Governor Cut-In", desc: "Release parking brake, pump to 100 PSI" },
  { id: 3, title: "Governor Cut-Out", desc: "Air builds to 120-140 PSI - governor stops compressor" },
  { id: 4, title: "Air Leakage Rate Test", desc: "Engine off - listen for air leaks" },
  { id: 5, title: "Static Test", desc: "1 minute hold - max 2 PSI loss" },
  { id: 6, title: "Applied Test", desc: "Apply brake 1 minute - max 3 PSI loss" },
  { id: 7, title: "Low Air Warning Test", desc: "Pump down - warning at 55 PSI or below" },
  { id: 8, title: "Spring Brake Test", desc: "Pump down - spring brake pops at 20-45 PSI" },
  { id: 9, title: "Air Build-Up Rate", desc: "Restart engine - 85 to 100 PSI in 45 seconds max" },
  { id: 10, title: "Parking Brake Test", desc: "Drive 1-2 ft, apply parking brake, bus stops" },
  { id: 11, title: "Service Brake Test", desc: "Drive at 5 mph, apply brake - smooth stop" }
];

export default function AirBrakeSkillsTest({ onHome }) {
  // ===== CORE SYSTEM STATE =====
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Air system simulation
  const [airPressure, setAirPressure] = useState(0);
  const [compressorRunning, setCompressorRunning] = useState(false);
  const [engineRunning, setEngineRunning] = useState(false);
  const [warningLightOn, setWarningLightOn] = useState(false);
  const [springBrakeEngaged, setSpringBrakeEngaged] = useState(true);

  // Bus controls
  const [parkingBrakeApplied, setParkingBrakeApplied] = useState(true);
  const [serviceBrakeApplied, setServiceBrakeApplied] = useState(false);
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [doorMasterOn, setDoorMasterOn] = useState(true);
  const [wheelChocked, setWheelChocked] = useState(false);
  const [transmissionInGear, setTransmissionInGear] = useState(false);
  const [busSpeed, setBusSpeed] = useState(0);
  const [seatbelt, setSeatbelt] = useState(false);

  // Test specific state
  const [testTime, setTestTime] = useState(0);
  const [testRunning, setTestRunning] = useState(false);
  const [testStartPressure, setTestStartPressure] = useState(0);
  const [testResults, setTestResults] = useState({});
  const [message, setMessage] = useState('🚌 Welcome to Air Brake Inspection Training');

  // Step 1
  const [step1Tasks, setStep1Tasks] = useState({
    chocked: false,
    doorsClosed: false,
    doorMasterOff: false,
    seated: false,
    masterRunNight: false,
    engineStarting: false,
    airBuilding: false
  });

  // Step 5 & 6
  const [initialPressureLoss, setInitialPressureLoss] = useState(0);
  const [additionalPressureLoss, setAdditionalPressureLoss] = useState(0);

  const timerRef = useRef(null);
  const airBuildRef = useRef(null);
  const leakageRef = useRef(null);
  const [sneezePlayed, setSneezeePlayed] = useState(false);

  // ===== AIR SYSTEM PHYSICS SIMULATION =====
  useEffect(() => {
    if (!engineRunning) {
      setCompressorRunning(false);
      if (airBuildRef.current) clearInterval(airBuildRef.current);
      setSneezeePlayed(false);
      return;
    }

    // Compressor behavior
    if (airPressure < 120) {
      setCompressorRunning(true);
      // Realistic air build: faster for training
      if (!airBuildRef.current) {
        airBuildRef.current = setInterval(() => {
          setAirPressure(prev => {
            const buildRate = prev < 85 ? 5 : (prev < 100 ? 3 : 1.5);
            const newPressure = Math.min(prev + buildRate, 120);
            return newPressure;
          });
        }, 100);
      }
    } else if (airPressure >= 120) {
      setCompressorRunning(false);
      if (airBuildRef.current) clearInterval(airBuildRef.current);
      // Show SNEEZE message and play sound when reaching full tank
      if (!sneezePlayed) {
        setSneezeePlayed(true);
        playSneezeSound();
        setMessage('💥 SNEEZE! GOVERNOR CUT-OUT! Air at FULL TANK (120 PSI) ✓');
      }
    }

    return () => {
      if (airBuildRef.current) clearInterval(airBuildRef.current);
    };
  }, [engineRunning]);

  // Warning light at 60 PSI, Spring brake at 20 PSI
  useEffect(() => {
    setWarningLightOn(airPressure <= 60 && airPressure > 0);
    setSpringBrakeEngaged(airPressure < 20);
  }, [airPressure]);

  // Test timer
  useEffect(() => {
    if (!testRunning) return;

    timerRef.current = setInterval(() => {
      setTestTime(t => t + 1);
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [testRunning]);

  // Air leakage when stationary
  useEffect(() => {
    if (!testRunning || !engineRunning || currentStep < 5 || currentStep > 6) return;

    leakageRef.current = setInterval(() => {
      setAirPressure(prev => {
        // Realistic leakage: 0.03 PSI per second = ~2 PSI per minute normal, up to 3 PSI under load
        const leakRate = serviceBrakeApplied ? 0.05 : 0.033;
        return Math.max(prev - leakRate, 0);
      });
    }, 1000);

    return () => clearInterval(leakageRef.current);
  }, [testRunning, engineRunning, currentStep, serviceBrakeApplied]);

  // ===== STEP HANDLERS =====

  const selectStep = (stepId) => {
    if (completedSteps.has(stepId)) return;
    if (stepId > 1 && !completedSteps.has(stepId - 1)) {
      setMessage(`⚠️ Complete Step ${stepId - 1} first!`);
      return;
    }
    setCurrentStep(stepId);
    resetStepState(stepId);
  };

  const resetStepState = (stepId) => {
    setTestTime(0);
    setTestRunning(false);
    setInitialPressureLoss(0);
    setAdditionalPressureLoss(0);

    switch (stepId) {
      case 1:
        setMessage('📋 STEP 1: Chock the right rear wheel with 3-point contact');
        break;
      case 2:
        setMessage('🔧 STEP 2: Release parking brake and pump to 100 PSI');
        break;
      case 3:
        setMessage('🔄 STEP 3: Allow air to build to Governor Cut-Out (120-140 PSI)');
        break;
      case 4:
        setMessage('🔧 STEP 4: Engine OFF - Listen for audible air leaks');
        break;
      case 5:
        setEngineRunning(true);
        setMessage('⏱️ STEP 5: Release parking brake - monitor 1 minute');
        break;
      case 6:
        setEngineRunning(true);
        setMessage('🔴 STEP 6: Apply service brake - monitor 1 minute');
        break;
      case 7:
        setMessage('📉 STEP 7: Pump down - warning light should come on at 60 PSI or below');
        break;
      case 8:
        setMessage('🛑 STEP 8: Continue pumping - spring brake pops between 20-45 PSI');
        break;
      case 9:
        setMessage('⚙️ STEP 9: Build pressure from 85 to 100 PSI in 45 seconds max');
        break;
      case 10:
        setEngineRunning(true);
        setMessage('🚌 STEP 10: Release parking brake - drive 1-2 feet, reapply brake');
        break;
      case 11:
        setEngineRunning(true);
        setMessage('🚙 STEP 11: Drive at 5 mph, apply service brake - smooth stop');
        break;
      default:
        break;
    }
  };

  const completeStep = (passed = true) => {
    if (passed) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      if (currentStep < 11) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        resetStepState(nextStep);
      } else {
        setMessage('🎉 INSPECTION COMPLETE! All 11 steps passed!');
      }
    } else {
      setMessage(`❌ Step ${currentStep} failed. Review requirements and try again.`);
    }
  };

  // ===== STEP 1: SECURING THE BUS =====
  const handleChockWheel = () => {
    setStep1Tasks(prev => ({ ...prev, chocked: true }));
    setMessage('✓ Right rear wheel chocked (3-point contact maintained)');
  };

  const handleCloseDoors = () => {
    setDoorsOpen(false);
    setStep1Tasks(prev => ({ ...prev, doorsClosed: true }));
    setMessage('✓ All doors closed securely');
  };

  const handleTurnOffDoorMaster = () => {
    setDoorMasterOn(false);
    setStep1Tasks(prev => ({ ...prev, doorMasterOff: true }));
    setMessage('✓ Door Master Switch turned OFF');
  };

  const handleGetInSeat = () => {
    setSeatbelt(true);
    setStep1Tasks(prev => ({ ...prev, seated: true }));
    setMessage('✓ In driver seat with seatbelt fastened');
  };

  const handleMasterRunNight = () => {
    setStep1Tasks(prev => ({ ...prev, masterRunNight: true }));
    setMessage('✓ Master Run Switch set to NIGHT RUN');
  };

  const handleStartEngine = () => {
    if (!step1Tasks.masterRunNight) {
      setMessage('⚠️ Set Master Run Switch to NIGHT RUN first');
      return;
    }
    playBusEngineSound();
    setEngineRunning(true);
    setCompressorRunning(true);
    setStep1Tasks(prev => ({ ...prev, engineStarting: true }));
    setMessage('🔧 Engine starting... air on doors, compressor building pressure');
  };

  const handleAirAtFullTank = () => {
    if (airPressure >= 120) {
      setStep1Tasks(prev => ({ ...prev, airBuilding: true }));
      completeStep(true);
    } else {
      setMessage(`⚠️ Air pressure ${Math.round(airPressure)} PSI - wait for full tank (~120 PSI)`);
    }
  };

  // ===== STEP 2: GOVERNOR CUT-IN =====
  const handleReleaseParkingBrake = () => {
    if (!engineRunning) {
      setMessage('⚠️ Engine must be running');
      return;
    }
    setParkingBrakeApplied(false);
    setMessage('🔧 Parking brake released. Now pump service brake to 100 PSI...');
  };

  const handlePumpServiceBrake = () => {
    if (airPressure >= 100) {
      setMessage('✓ Pressure at 100 PSI - Governor cut-in detected!');
      return;
    }
    // Each pump adds ~5 PSI but slows down as pressure increases
    const currentRate = airPressure < 50 ? 8 : (airPressure < 85 ? 6 : 4);
    setAirPressure(prev => Math.min(prev + currentRate, 100));
  };

  const handleReapplyParkingBrake = () => {
    if (airPressure >= 100) {
      setParkingBrakeApplied(true);
      setMessage('✓ Parking brake reapplied at 100+ PSI');
      completeStep(true);
    } else {
      setMessage(`⚠️ Wait until pressure reaches 100 PSI (currently ${Math.round(airPressure)} PSI)`);
    }
  };

  // ===== STEP 3: GOVERNOR CUT-OUT =====
  const handleCheckGovernorCutOut = () => {
    if (airPressure >= 120 && airPressure <= 140) {
      setMessage(`✓ Governor Cut-Out verified at ${Math.round(airPressure)} PSI (Normal: 120-140 PSI)`);
      completeStep(true);
    } else {
      setMessage(`❌ Governor Cut-Out should be 120-140 PSI (currently ${Math.round(airPressure)} PSI)`);
      completeStep(false);
    }
  };

  // ===== STEP 4: AIR LEAKAGE RATE TEST =====
  const handleEngineOff = () => {
    setEngineRunning(false);
    setCompressorRunning(false);
    setMessage('🔧 Engine OFF, doors CLOSED. Listen carefully for air leaks...');
  };

  const handleNoLeaks = () => {
    setMessage('✓ No audible air leaks detected');
    completeStep(true);
  };

  const handleLeaksDetected = () => {
    setMessage('❌ Audible air leaks detected - DO NOT operate bus');
    completeStep(false);
  };

  // ===== STEP 5: STATIC TEST =====
  const handleStartStaticTest = () => {
    if (!engineRunning) {
      setMessage('⚠️ Engine must be running');
      return;
    }
    if (parkingBrakeApplied) {
      setMessage('⚠️ Release parking brake first');
      return;
    }

    setTestTime(0);
    setTestStartPressure(airPressure);
    setInitialPressureLoss(airPressure - (airPressure - (Math.random() * 10 + 5)));
    setTestRunning(true);
    setMessage('⏱️ STATIC TEST RUNNING - 60 seconds. Watch gauge for pressure loss...');

    setTimeout(() => {
      setTestRunning(false);
      const loss = testStartPressure - airPressure;
      if (loss <= 2) {
        setAdditionalPressureLoss(loss);
        setMessage(`✓ Static Test PASSED: ${Math.round(loss * 10) / 10} PSI loss (max 2 allowed)`);
        completeStep(true);
      } else {
        setAdditionalPressureLoss(loss);
        setMessage(`❌ Static Test FAILED: ${Math.round(loss * 10) / 10} PSI loss (max 2 allowed)`);
        completeStep(false);
      }
    }, 60000);
  };

  // ===== STEP 6: APPLIED TEST =====
  const handleApplyServiceBrake = () => {
    if (!engineRunning) {
      setMessage('⚠️ Engine must be running');
      return;
    }

    setServiceBrakeApplied(true);
    setTestTime(0);
    setTestStartPressure(airPressure);

    // Simulate initial pressure loss (5-15 PSI)
    const initialLoss = Math.random() * 10 + 5;
    setInitialPressureLoss(initialLoss);

    setMessage(`🔴 Service brake applied. Initial loss: ${Math.round(initialLoss)} PSI (normal: 5-15 PSI). Hold steady for 60 seconds...`);

    setTimeout(() => {
      setTestRunning(true);
      setMessage('⏱️ APPLIED TEST RUNNING - 60 seconds. Maintain steady brake pressure...');

      setTimeout(() => {
        setTestRunning(false);
        setServiceBrakeApplied(false);
        const loss = testStartPressure - airPressure;
        if (loss <= 3) {
          setAdditionalPressureLoss(Math.round((loss - initialLoss) * 10) / 10);
          setMessage(`✓ Applied Test PASSED: ${Math.round((loss - initialLoss) * 10) / 10} PSI additional loss (max 3 allowed)`);
          completeStep(true);
        } else {
          setAdditionalPressureLoss(Math.round((loss - initialLoss) * 10) / 10);
          setMessage(`❌ Applied Test FAILED: ${Math.round((loss - initialLoss) * 10) / 10} PSI additional loss (max 3 allowed)`);
          completeStep(false);
        }
      }, 60000);
    }, 500);
  };

  // ===== STEP 7: LOW AIR WARNING TEST =====
  const handlePumpDown = () => {
    if (airPressure > 0) {
      const newPressure = Math.max(airPressure - 20, 0);
      setAirPressure(newPressure);
      if (newPressure <= 60 && airPressure > 60) {
        setMessage(`✓ Low Air Warning light ON at ${Math.round(newPressure)} PSI`);
        completeStep(true);
      }
    }
  };

  // ===== STEP 8: SPRING BRAKE TEST =====
  const handleContinuePumpDown = () => {
    if (airPressure > 0) {
      const newPressure = Math.max(airPressure - 15, 0);
      setAirPressure(newPressure);
      if (newPressure <= 40 && newPressure >= 20 && springBrakeEngaged) {
        setMessage(`✓ Spring Brake engaged at ${Math.round(newPressure)} PSI (Normal: 20-40 PSI)`);
        completeStep(true);
      } else if (newPressure < 20 && springBrakeEngaged) {
        setMessage(`✓ Spring Brake engaged at ${Math.round(newPressure)} PSI`);
        completeStep(true);
      }
    }
  };

  // ===== STEP 9: AIR BUILD-UP RATE =====
  const handleStartBuildTest = () => {
    setEngineRunning(true);
    setAirPressure(85);
    setTestTime(0);
    setTestRunning(true);
    setMessage('⚙️ Engine started at 85 PSI. Building air at 1/3 throttle. Watch for 100 PSI within 45 seconds...');

    setTimeout(() => {
      setTestRunning(false);
      if (airPressure >= 100 && testTime <= 45) {
        setMessage(`✓ Build-Up Test PASSED: Reached 100 PSI in ${testTime} seconds (max 45 allowed)`);
        completeStep(true);
      } else {
        setMessage(`❌ Build-Up Test FAILED: Reached ${Math.round(airPressure)} PSI in ${testTime} seconds`);
        completeStep(false);
      }
    }, 45000);
  };

  // ===== STEP 10: PARKING BRAKE TEST =====
  const handleDrive1To2Feet = () => {
    if (airPressure < 100) {
      setMessage('⚠️ Build air to full tank first');
      return;
    }
    setBusSpeed(3);
    setTransmissionInGear(true);
    setMessage('🚌 Driving 1-2 feet... Now reapply parking brake');

    setTimeout(() => {
      setParkingBrakeApplied(true);
      setTransmissionInGear(false);
      setBusSpeed(0);
      setMessage('✓ Bus stopped with parking brake - TEST PASSED');
      completeStep(true);
    }, 2000);
  };

  // ===== STEP 11: SERVICE BRAKE TEST =====
  const handleDriveAt5MPH = () => {
    if (airPressure < 100) {
      setMessage('⚠️ Build air to full tank first');
      return;
    }
    setBusSpeed(5);
    setTransmissionInGear(true);
    setMessage('🚙 Driving at 5 mph... Apply service brake now');
  };

  const handleApplyBrakeWhileDriving = () => {
    setServiceBrakeApplied(true);
    setTimeout(() => {
      setBusSpeed(0);
      setTransmissionInGear(false);
      setServiceBrakeApplied(false);
      setMessage('✓ Bus stopped smoothly with no pulling or unusual feel - TEST PASSED');
      completeStep(true);
    }, 3000);
  };

  // ===== UI HELPERS =====
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const AirGauge = ({ psi = 0, size = 200 }) => {
    const angle = -90 + (psi / 140) * 180;
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 35%, #1a3a52, #0a0f1a)',
        border: '8px solid #3a6a8a', position: 'relative',
        boxShadow: '0 0 20px rgba(0,0,0,0.7), inset 0 0 20px rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {/* Needle */}
        <div style={{
          position: 'absolute', width: 6, height: size * 0.4,
          background: 'linear-gradient(to top, #ff6b6b, #ee5a52)',
          bottom: '50%', left: '50%', transformOrigin: 'center bottom',
          transform: `translateX(-50%) rotate(${angle}deg)`,
          borderRadius: 3, boxShadow: '0 0 10px rgba(255, 107, 107, 0.6)',
          zIndex: 2
        }} />

        {/* PSI Display */}
        <div style={{
          textAlign: 'center', zIndex: 1,
          color: psi > 100 ? '#ffaa33' : (psi > 55 ? '#44ff44' : '#ff4444'),
          fontWeight: 'bold'
        }}>
          <div style={{ fontSize: '2rem', fontFamily: 'monospace' }}>
            {Math.round(psi)}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#8ab4c0' }}>PSI</div>
        </div>

        {/* Zones */}
        <div style={{
          position: 'absolute', width: '100%', height: '100%',
          borderRadius: '50%', opacity: 0.3, pointerEvents: 'none',
          background: `conic-gradient(
            red 0deg 26deg,
            green 26deg 155deg,
            orange 155deg 180deg
          )`
        }} />
      </div>
    );
  };

  const StepButton = ({ onClick, label, enabled = true, danger = false }) => (
    <button
      onClick={onClick}
      disabled={!enabled}
      style={{
        width: '100%', padding: '16px 12px', marginBottom: 10,
        background: danger ? '#4a2a2a' : (enabled ? '#2a6a8a' : '#1a4a6a'),
        color: danger ? '#ff9999' : '#e4f4ff',
        border: `2px solid ${danger ? '#cc6a6a' : (enabled ? '#6abadb' : '#2a5a7a')}`,
        borderRadius: 10, fontSize: '1rem', fontWeight: 700,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.4,
        transition: 'all 0.2s',
        boxShadow: enabled ? '0 4px 12px rgba(74, 178, 210, 0.3)' : 'none',
        textAlign: 'left',
        lineHeight: 1.4
      }}
      onMouseEnter={(e) => {
        if (enabled) e.target.style.background = danger ? '#5a3a3a' : '#3a7a9a';
      }}
      onMouseLeave={(e) => {
        if (enabled) e.target.style.background = danger ? '#4a2a2a' : '#2a6a8a';
      }}
    >
      {label}
    </button>
  );

  // ===== RENDER MAIN VIEW =====
  return (
    <div style={{
      width: '100%', height: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #1a2a4a 100%)',
      display: 'flex', flexDirection: 'column', color: '#d4e4f7', fontFamily: 'Segoe UI, sans-serif',
      overflow: 'hidden'
    }}>
      {/* HEADER */}
      <div style={{
        background: 'linear-gradient(180deg, #1f3a52 0%, #0f1f35 100%)',
        padding: '16px 24px', borderBottom: '2px solid #2a5a7a',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.3rem', color: '#e4f2ff' }}>
            🚌 CDL AIR BRAKE INSPECTION - REALISTIC SIMULATOR
          </h1>
          <small style={{ color: '#8ab4c0' }}>Step {currentStep} of 11 • Follow real bus driver procedures</small>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{
            background: '#1a3f45', padding: '8px 16px', borderRadius: 8,
            fontSize: '0.85rem', color: airPressure > 55 ? '#4af8d9' : '#ff6a6a'
          }}>
            🔍 PSI: {Math.round(airPressure)}
          </div>
          <button onClick={onHome} style={{
            padding: '8px 16px', background: '#1f3a4a', color: '#a4d4ff',
            border: '1px solid #2a5a7a', borderRadius: 8, cursor: 'pointer', fontWeight: 600
          }}>
            🏠 Home
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: 'flex', gap: 16, padding: 16, overflow: 'hidden' }}>
        {/* LEFT: DASHBOARD & GAUGE */}
        <div style={{
          flex: '0 0 45%', background: 'radial-gradient(ellipse at 30% 40%, #2a4a6a, #0f1f35)',
          borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 16, border: '1px solid #2a5a7a', overflow: 'auto'
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#a4d4ff' }}>
            🔍 AIR PRESSURE GAUGE (Reference front gauge on left)
          </div>

          <AirGauge psi={airPressure} size={280} />

          {/* BIG PRESSURE DISPLAY */}
          <div style={{
            background: '#1a3f45', padding: '16px', borderRadius: 12,
            textAlign: 'center', border: '2px solid #4a9a85'
          }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#4af8d9', fontFamily: 'monospace' }}>
              {Math.round(airPressure)} PSI
            </div>
            <div style={{ fontSize: '0.9rem', color: '#8ab4c0', marginTop: 8 }}>
              {airPressure < 55 ? '🔴 LOW PRESSURE' :
               airPressure < 120 ? '🟡 BUILDING' :
               '✅ FULL TANK'}
            </div>
          </div>

          {/* PROGRESS BAR */}
          <div style={{
            background: '#0a1428', padding: 12, borderRadius: 8
          }}>
            <div style={{
              width: '100%', height: 30, background: '#1a3f45', borderRadius: 6,
              overflow: 'hidden', border: '1px solid #2a5a7a'
            }}>
              <div style={{
                height: '100%', background: 'linear-gradient(90deg, #ff4444, #ffaa33, #44ff44)',
                width: `${Math.min((airPressure / 130) * 100, 100)}%`,
                transition: 'width 0.1s linear',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#000', fontWeight: 'bold', fontSize: '0.9rem'
              }}>
                {airPressure >= 13 ? `${Math.round((airPressure / 130) * 100)}%` : ''}
              </div>
            </div>
            <div style={{
              marginTop: 8, color: '#8ab4c0', fontSize: '0.85rem', textAlign: 'center'
            }}>
              {airPressure < 120 ? `Building to 120 PSI...` : `✓ Ready to continue!`}
            </div>
          </div>

          {/* STATUS INDICATORS */}
          <div style={{
            width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
            marginTop: 16
          }}>
            <div style={{
              background: engineRunning ? '#2a5a5a' : '#3f2a2a',
              padding: '10px', borderRadius: 8,
              color: engineRunning ? '#4af8d9' : '#f4a45a',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              {engineRunning ? '✓ ENGINE ON' : '⊘ ENGINE OFF'}
            </div>
            <div style={{
              background: compressorRunning ? '#2a5a5a' : '#1a2f45',
              padding: '10px', borderRadius: 8,
              color: compressorRunning ? '#4af8d9' : '#a4d4ff',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              {compressorRunning ? '⚙️ COMPRESSOR' : '⊘ COMPRESSOR'}
            </div>
            <div style={{
              background: parkingBrakeApplied ? '#3f2a2a' : '#2a5a5a',
              padding: '10px', borderRadius: 8,
              color: parkingBrakeApplied ? '#f4a45a' : '#4af8d9',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              {parkingBrakeApplied ? '🛑 PARK BRAKE ON' : '✓ PARK OFF'}
            </div>
            <div style={{
              background: warningLightOn ? '#5a2a2a' : '#1a2f45',
              padding: '10px', borderRadius: 8,
              color: warningLightOn ? '#ff6a6a' : '#a4d4ff',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              {warningLightOn ? '🚨 WARNING ON' : '⊘ WARNING'}
            </div>
            <div style={{
              background: springBrakeEngaged ? '#3f2a2a' : '#2a5a5a',
              padding: '10px', borderRadius: 8,
              color: springBrakeEngaged ? '#f4a45a' : '#4af8d9',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              {springBrakeEngaged ? '🛑 SPRING BRAKE ON' : '✓ SPRING OFF'}
            </div>
            <div style={{
              background: busSpeed > 0 ? '#2a5a5a' : '#1a2f45',
              padding: '10px', borderRadius: 8,
              color: busSpeed > 0 ? '#4af8d9' : '#a4d4ff',
              fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
            }}>
              🚙 {busSpeed} mph
            </div>
          </div>

          {/* MESSAGE */}
          <div style={{
            width: '100%', background: '#0a1428', padding: 12, borderRadius: 8,
            color: '#a4d4ff', fontSize: '0.9rem', textAlign: 'center', marginTop: 'auto',
            minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1.4
          }}>
            {message}
          </div>
        </div>

        {/* RIGHT: INTERACTIVE CONTROLS & STEPS */}
        <div style={{
          flex: '1 1 55%', background: 'linear-gradient(135deg, #0f1f35 0%, #1a2a45 100%)',
          borderRadius: 16, padding: 16, border: '2px solid #4a8aaa',
          display: 'flex', flexDirection: 'column', overflow: 'auto', gap: 12,
          minHeight: 400
        }}>
          {/* STEP TITLE BANNER */}
          <div style={{
            background: 'linear-gradient(90deg, #2a5a7a 0%, #1a3f45 100%)',
            padding: '12px 16px', borderRadius: 8,
            borderLeft: '4px solid #4af8d9',
            fontWeight: 700, fontSize: '1rem', color: '#e4f2ff'
          }}>
            📋 STEP {currentStep} OF 11
          </div>

          {/* STEP CONTENT */}
          <div style={{ marginBottom: 0, flex: 1 }}>
            {currentStep === 1 && (
              <div>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '1.3rem', fontWeight: 900, color: '#e4f2ff', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                  🔧 STEP 1: SECURING THE BUS
                </h2>
                <div style={{ fontSize: '1rem', color: '#a4d4ff', marginBottom: 16, lineHeight: 1.5, fontWeight: 600, background: '#1a3f45', padding: 12, borderRadius: 8, borderLeft: '3px solid #4af8d9' }}>
                  ⚠️ Complete all tasks in order.<br/>Maintain 3-point contact when outside vehicle.
                </div>
                <StepButton
                  onClick={handleChockWheel}
                  label={`${step1Tasks.chocked ? '✓' : '1'} Chock Right Rear Wheel (3-Point Contact)`}
                  enabled={!step1Tasks.chocked}
                />
                <StepButton
                  onClick={handleCloseDoors}
                  label={`${step1Tasks.doorsClosed ? '✓' : '2'} Close All Doors`}
                  enabled={step1Tasks.chocked && !step1Tasks.doorsClosed}
                />
                <StepButton
                  onClick={handleTurnOffDoorMaster}
                  label={`${step1Tasks.doorMasterOff ? '✓' : '3'} Door Master Switch OFF`}
                  enabled={step1Tasks.doorsClosed && !step1Tasks.doorMasterOff}
                />
                <StepButton
                  onClick={handleGetInSeat}
                  label={`${step1Tasks.seated ? '✓' : '4'} Get in Seat + Seatbelt`}
                  enabled={step1Tasks.doorMasterOff && !step1Tasks.seated}
                />
                <StepButton
                  onClick={handleMasterRunNight}
                  label={`${step1Tasks.masterRunNight ? '✓' : '5'} Master Run → NIGHT RUN`}
                  enabled={step1Tasks.seated && !step1Tasks.masterRunNight}
                />
                <StepButton
                  onClick={handleStartEngine}
                  label={`${step1Tasks.engineStarting ? '✓' : '6'} Press START Button`}
                  enabled={step1Tasks.masterRunNight && !step1Tasks.engineStarting}
                />
                <StepButton
                  onClick={handleAirAtFullTank}
                  label={`${step1Tasks.airBuilding ? '✓' : '7'} Air at Full Tank (~120 PSI) - Continue`}
                  enabled={step1Tasks.engineStarting && !step1Tasks.airBuilding}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🔧 STEP 2: GOVERNOR CUT-IN
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Pump service brake until pressure reaches 100 PSI. Governor will cut in.
                </div>
                <StepButton
                  onClick={handleReleaseParkingBrake}
                  label="Release Parking Brake"
                  enabled={!parkingBrakeApplied}
                />
                <StepButton
                  onClick={handlePumpServiceBrake}
                  label={`Pump Service Brake (${Math.round(airPressure)} PSI)`}
                  enabled={!parkingBrakeApplied && airPressure < 100}
                />
                <div style={{
                  background: '#1a3f35', padding: 12, borderRadius: 8, marginBottom: 8,
                  color: '#4af8d9', fontSize: '0.85rem', fontWeight: 600
                }}>
                  {airPressure >= 100 ? '✓ Governor Cut-In Detected at 100 PSI' : `Building to 100 PSI... (${Math.round(airPressure)})`}
                </div>
                <StepButton
                  onClick={handleReapplyParkingBrake}
                  label="Reapply Parking Brake → Continue"
                  enabled={airPressure >= 100}
                />
              </div>
            )}

            {currentStep === 3 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🔄 STEP 3: GOVERNOR CUT-OUT
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Air pressure builds to governor cut-out (120-140 PSI). Compressor stops.
                </div>
                <div style={{
                  background: '#2a5a5a', padding: 12, borderRadius: 8, marginBottom: 8,
                  color: '#4af8d9', fontSize: '0.9rem', fontWeight: 600, textAlign: 'center'
                }}>
                  {airPressure >= 120 ? `✓ Governor Cut-Out at ${Math.round(airPressure)} PSI` : `Building pressure... ${Math.round(airPressure)}/120 PSI`}
                </div>
                <StepButton
                  onClick={handleCheckGovernorCutOut}
                  label="Verify Cut-Out Pressure"
                  enabled={airPressure >= 120}
                />
              </div>
            )}

            {currentStep === 4 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🔧 STEP 4: AIR LEAKAGE RATE TEST
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Turn engine OFF. Listen for hissing sounds or air leaks. Open window.
                </div>
                <StepButton
                  onClick={handleEngineOff}
                  label="Turn Engine OFF"
                  enabled={engineRunning}
                />
                <div style={{
                  background: '#3f2a2a', padding: 12, borderRadius: 8, marginBottom: 8,
                  color: '#f4a45a', fontSize: '0.85rem', fontWeight: 600, textAlign: 'center'
                }}>
                  🎧 Listen carefully for audible leaks...
                </div>
                <StepButton
                  onClick={handleNoLeaks}
                  label="✓ No Audible Leaks Detected"
                />
                <StepButton
                  onClick={handleLeaksDetected}
                  label="✗ Audible Leaks Detected"
                  danger
                />
              </div>
            )}

            {currentStep === 5 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  ⏱️ STEP 5: STATIC TEST (1 minute, max 2 PSI loss)
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Release parking brake. Monitor pressure for 60 seconds. Should lose ≤2 PSI.
                </div>
                <StepButton
                  onClick={handleReleaseParkingBrake}
                  label="Release Parking Brake"
                  enabled={!testRunning && parkingBrakeApplied}
                />
                <StepButton
                  onClick={handleStartStaticTest}
                  label={testRunning ? `TEST RUNNING: ${testTime}s` : "Start 60-Second Test"}
                  enabled={!parkingBrakeApplied && !testRunning}
                />
                {initialPressureLoss > 0 && (
                  <div style={{
                    background: '#1a3f35', padding: 12, borderRadius: 8,
                    color: '#4af8d9', fontSize: '0.85rem', fontWeight: 600
                  }}>
                    Initial loss: {Math.round(initialPressureLoss)} PSI<br/>
                    Additional loss: {Math.round(additionalPressureLoss * 10) / 10} PSI
                  </div>
                )}
              </div>
            )}

            {currentStep === 6 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🔴 STEP 6: APPLIED TEST (1 minute, max 3 PSI loss)
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Apply and HOLD service brake. Monitor loss for 60 seconds. Should lose ≤3 PSI.
                </div>
                <StepButton
                  onClick={handleApplyServiceBrake}
                  label={testRunning ? `TEST RUNNING: ${testTime}s` : "Apply Service Brake (Hold Down)"}
                  enabled={!testRunning}
                />
                {initialPressureLoss > 0 && (
                  <div style={{
                    background: '#1a3f35', padding: 12, borderRadius: 8,
                    color: '#4af8d9', fontSize: '0.85rem', fontWeight: 600
                  }}>
                    Initial loss: {Math.round(initialPressureLoss)} PSI<br/>
                    Additional loss: {Math.round(additionalPressureLoss * 10) / 10} PSI
                  </div>
                )}
              </div>
            )}

            {currentStep === 7 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  📉 STEP 7: LOW AIR WARNING TEST
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Pump down service brake. Warning light should come on at 55 PSI or BELOW.
                </div>
                <StepButton
                  onClick={handlePumpDown}
                  label={`Pump Down (Current: ${Math.round(airPressure)} PSI)`}
                  enabled={airPressure > 0 && !warningLightOn}
                />
                {warningLightOn && (
                  <div style={{
                    background: '#5a2a2a', padding: 12, borderRadius: 8,
                    color: '#ff6a6a', fontSize: '0.9rem', fontWeight: 600, textAlign: 'center'
                  }}>
                    🚨 LOW AIR WARNING LIGHT ON at {Math.round(airPressure)} PSI
                  </div>
                )}
              </div>
            )}

            {currentStep === 8 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🛑 STEP 8: SPRING BRAKE TEST
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Continue pumping down. Spring brake should pop between 20-45 PSI.
                </div>
                <StepButton
                  onClick={handleContinuePumpDown}
                  label={`Pump Down (Current: ${Math.round(airPressure)} PSI)`}
                  enabled={airPressure > 0 && !springBrakeEngaged}
                />
                {springBrakeEngaged && (
                  <div style={{
                    background: '#2a5a5a', padding: 12, borderRadius: 8,
                    color: '#4af8d9', fontSize: '0.9rem', fontWeight: 600, textAlign: 'center'
                  }}>
                    ✓ SPRING BRAKE ENGAGED at {Math.round(airPressure)} PSI
                  </div>
                )}
              </div>
            )}

            {currentStep === 9 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  ⚙️ STEP 9: AIR BUILD-UP RATE TEST
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Start engine at 85 PSI. Build to 100 PSI within 45 seconds using 1/3 throttle.
                </div>
                <StepButton
                  onClick={handleStartBuildTest}
                  label={testRunning ? `TEST RUNNING: ${testTime}s (${Math.round(airPressure)} PSI)` : "Start Engine & Air Build Test"}
                  enabled={!testRunning}
                />
              </div>
            )}

            {currentStep === 10 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🚌 STEP 10: PARKING BRAKE TEST
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Put transmission in Drive. Release parking brake. Drive 1-2 feet, then reapply brake.
                </div>
                <StepButton
                  onClick={handleDrive1To2Feet}
                  label={`Drive 1-2 Feet (${busSpeed} mph)`}
                  enabled={!transmissionInGear && airPressure >= 100}
                />
              </div>
            )}

            {currentStep === 11 && (
              <div>
                <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#e4f2ff' }}>
                  🚙 STEP 11: SERVICE BRAKE TEST (FINAL)
                </h2>
                <div style={{ fontSize: '0.85rem', color: '#8ab4c0', marginBottom: 12 }}>
                  Drive at 5 mph. Apply service brake. Should stop smoothly with no pulling.
                </div>
                <StepButton
                  onClick={handleDriveAt5MPH}
                  label={`Drive at 5 MPH (${busSpeed} mph)`}
                  enabled={!transmissionInGear && airPressure >= 100}
                />
                <StepButton
                  onClick={handleApplyBrakeWhileDriving}
                  label={`Apply Service Brake`}
                  enabled={transmissionInGear && busSpeed > 0}
                />
              </div>
            )}
          </div>

          {/* STEPS LIST */}
          <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #2a5a7a' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#a4d4ff' }}>11 Steps Progress:</h3>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
              fontSize: '0.75rem'
            }}>
              {MAIN_STEPS.map((step) => (
                <button
                  key={step.id}
                  onClick={() => selectStep(step.id)}
                  disabled={completedSteps.has(step.id)}
                  style={{
                    padding: 8, borderRadius: 6,
                    background: currentStep === step.id ? '#2a5a7a' : (completedSteps.has(step.id) ? '#1a3f35' : '#1a2f45'),
                    color: completedSteps.has(step.id) ? '#4af8d9' : '#a4d4ff',
                    border: `1px solid ${currentStep === step.id ? '#6a9aba' : (completedSteps.has(step.id) ? '#4a8a75' : '#2a5a7a')}`,
                    cursor: 'pointer', fontWeight: 600,
                    opacity: completedSteps.has(step.id) ? 0.8 : 1
                  }}
                >
                  {completedSteps.has(step.id) ? '✓' : step.id} Step {step.id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
